import { useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { SlideOver } from '@/components/Shared/SlideOver';
import { Button } from '@/components/Shared/Button';
import { Badge } from '@/components/Shared/Badge';
import { TextArea } from '@/components/Shared/TextArea';
import { Loading } from '@/components/Shared/Loading';
import { EmptyState } from '@/components/Shared/EmptyState';
import { useOpenItemsStore } from '@/store/useOpenItemsStore';
import type { OpenItemDto } from '@/store/useOpenItemsStore';
import { formatDateTime } from '@/utils/dateUtils';
import { formatDueDate } from '@/pages/OpenItems/openItemText';

/** Mirrors OpenItemMessage.TextMaxLength on the backend. */
const MAX_MESSAGE = 2000;

interface OpenItemMessagesProps {
  /** The item whose thread is shown; null closes the view. */
  item: OpenItemDto | null;
  onClose: () => void;
}

/**
 * Message thread of a manual open item — the assignee tells Verwaltung why something cannot be
 * done, Verwaltung answers. Separate from the edit form on purpose: the assignee may write here
 * but must not change title, note or due date. Opening it marks the thread as read.
 */
export default function OpenItemMessages({ item, onClose }: OpenItemMessagesProps) {
  const { t } = useTranslation(['openItems', 'common']);
  const thread = useOpenItemsStore((s) => s.thread);
  const threadLoading = useOpenItemsStore((s) => s.threadLoading);
  const threadError = useOpenItemsStore((s) => s.threadError);
  const sending = useOpenItemsStore((s) => s.sendingMessage);
  const openThread = useOpenItemsStore((s) => s.openThread);
  const sendMessage = useOpenItemsStore((s) => s.sendMessage);
  const closeThread = useOpenItemsStore((s) => s.closeThread);

  const [text, setText] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  const itemId = item?.id ?? null;
  const completed = !!item?.completedAt;
  const messages = thread?.itemId === itemId ? thread.messages : [];

  useEffect(() => {
    if (!itemId) return;
    setText('');
    void openThread(itemId);
    return () => closeThread();
  }, [itemId, openThread, closeThread]);

  // Keep the newest message in view, like any chat.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const handleSend = async () => {
    if (!itemId || !text.trim()) return;
    const sent = await sendMessage(itemId, text.trim());
    if (sent) setText('');
  };

  const footer = completed ? (
    <p className="text-[12px] text-slate-500">{t('openItems:thread.completedHint')}</p>
  ) : (
    <div className="space-y-2">
      <TextArea
        label={t('openItems:thread.placeholder')}
        name="message"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Ctrl/Cmd+Enter sends; a plain Enter stays a line break.
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void handleSend();
          }
        }}
        maxLength={MAX_MESSAGE}
        rows={3}
      />
      <div className="flex justify-end">
        <Button
          onClick={() => void handleSend()}
          disabled={!text.trim() || sending}
          loading={sending}
          leftIcon={<Send className="w-4 h-4" strokeWidth={2} />}
        >
          {t('openItems:thread.send')}
        </Button>
      </div>
    </div>
  );

  return (
    <SlideOver
      isOpen={item !== null}
      onClose={onClose}
      title={t('openItems:thread.title')}
      size="md"
      hasUnsavedChanges={text.trim().length > 0 && !sending}
      footer={footer}
    >
      {item && (
        <div className="space-y-5">
          {/* The task itself, read-only — changing it stays in the edit form. */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1">
            <div className="flex items-center gap-2">
              <span
                className={`text-[15px] font-semibold ${
                  completed ? 'text-slate-400 line-through' : 'text-slate-900'
                }`}
              >
                {item.title}
              </span>
              {completed && <Badge variant="success">{t('openItems:thread.completed')}</Badge>}
            </div>
            {item.note && (
              <p className="text-[13px] text-slate-600 whitespace-pre-line">{item.note}</p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate-500">
              <span>
                {t('openItems:fields.assignee')}: {item.assigneeName || '–'}
              </span>
              <span className="tabular-nums">
                {t('openItems:fields.due')}: {formatDueDate(item.dueDate)}
              </span>
            </div>
          </div>

          {threadLoading && <Loading />}

          {threadError && !threadLoading && (
            <EmptyState
              variant="error"
              title={t(threadError)}
              onRetry={() => itemId && void openThread(itemId)}
            />
          )}

          {!threadLoading && !threadError && messages.length === 0 && (
            <p className="text-[13px] text-slate-500">{t('openItems:thread.empty')}</p>
          )}

          {!threadLoading && !threadError && messages.length > 0 && (
            <ul className="space-y-3">
              {messages.map((message) => (
                <li
                  key={message.id}
                  className={`flex ${message.isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-xl border px-3 py-2 ${
                      message.isOwn
                        ? 'border-amber-200 bg-amber-50'
                        : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 text-[11px] text-slate-500">
                      <span className="font-medium text-slate-700">
                        {message.authorName || '–'}
                      </span>
                      {!message.fromAssignee && (
                        <span className="text-slate-400">{t('openItems:thread.office')}</span>
                      )}
                      <span className="tabular-nums">
                        {formatDateTime(message.createdAt, {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[13px] text-slate-900 whitespace-pre-line break-words">
                      {message.text}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div ref={endRef} />
        </div>
      )}
    </SlideOver>
  );
}
